'use strict'

import Utils from './../Utils.js'
import Dropcolor from './../Dropcolor.js'
import DropHandler from './DropHandler.js'
import ExportHandler from './ExportHandler.js'
import SaveProjectHandler from './SaveProjectHandler.js'
import DrawingToolActionHandler from './DrawingToolActionHandler.js'
import PaintingUIBinder from './PaintingUIBinder.js'
import CXImageAction from './../ImagePaintProcessing/CXImageAction.js'
import Config from './../Config.js'

/**
 * PaintingListener coordinates painting UI events, drop interactions, and export/save actions.
 * It composes specialized handlers (drop, export, save, drawing) behind one runtime entrypoint.
 */
class PaintingListener {
    /**
     * @param {Object} application - Visualizer Application instance (Application.js).
     * @param {Document|HTMLElement} [root=document] - DOM root to scope queries/events; defaults to document.
     */
    constructor(application, root = document) {
        this.application = application
        this.root = root
        this.dropHandler = new DropHandler(application, root)
        this.exportHandler = new ExportHandler(application)
        this.saveHandler = new SaveProjectHandler(application, this._stripPrepaintParams.bind(this))
        this.tools = new DrawingToolActionHandler(application)
        this.ui = new PaintingUIBinder({
            application,
            root,
            onToolSelect: (tool) => this._selectTool(tool),
            onExportJpeg: () => this.exportHandler.generateJpeg(),
            onExportPdf: () => this.exportHandler.generatePdf(),
            onExportProject: (nameInput) => this.exportHandler.exportProject(nameInput),
            onSave: () => this.saveHandler.saveProjectAndInfos(),
            onSlotClick: (el) => this.dropHandler.handleSlotClick(el),
            onSlotDrop: (event, ui, id) => this.dropHandler.handleSlotDrop(event, ui, id),
            onCanvasDrop: (ui) => this.dropHandler.handleCanvasDrop(ui),
            onLoginRequired: () => this._showModal('loginModal'),
            onVerifyRequired: () => this._showModal('userLoggedButEmailNotVerifiedModal'),
            onBuy: () => this._handleBuy(),
            onDeleteSlot: (event) => this._handleDeleteDropColorClick(event),
            toolActions: this.tools,
        })

        this.ui.wireCoreSelectors()
        this.ui.wireToolButtons()
        this.ui.wirePickers()
        this.ui.wireExporters()
        this.ui.wireCanvasPointerEvents()
        this.ui.wireDropHandlers()
        this.ui.wireMisc()
    }

    // ──────────────────────────────────────────────
    // Static helpers
    // ──────────────────────────────────────────────

    /**
     * STATIC: Translate touch to mouse events for draggable tiles (color/texture).
     * @param {TouchEvent} e - Native touch event.
     */
    static touchToMouseTile(e) {
        const map = { touchstart: 'mousedown', touchmove: 'mousemove', touchend: 'mouseup' }
        const type = map[e.type]
        if (!type) return

        for (const t of e.changedTouches) {
            const target = document.elementFromPoint(t.clientX, t.clientY) || e.target
            const ev = new MouseEvent(type, {
                bubbles: true,
                cancelable: true,
                view: window,
                detail: 1,
                screenX: t.screenX,
                screenY: t.screenY,
                clientX: t.clientX,
                clientY: t.clientY,
                ctrlKey: e.ctrlKey,
                altKey: e.altKey,
                shiftKey: e.shiftKey,
                metaKey: e.metaKey,
                button: 0,
                buttons: 1,
            })
            target.dispatchEvent(ev)
        }

        e.preventDefault()
        e.stopPropagation()
    }

    /**
     * STATIC: Wire touch→mouse bridging on color/texture tiles.
     * @param {Document|HTMLElement} [root=document] - Scope for draggable tiles.
     */
    static addTouchEventToDropColor(root = document) {
        const wire = (collection) => {
            for (const el of Array.from(collection)) {
                const tgt = el.firstElementChild || el
                // Start a synthetic mouse drag when the user touches a swatch or texture.
                tgt.addEventListener('touchstart', PaintingListener.touchToMouseTile, { passive: false })
                // Keep the synthetic drag in sync with the finger position.
                tgt.addEventListener('touchmove', PaintingListener.touchToMouseTile, { passive: false })
                // End the synthetic drag when the touch interaction finishes.
                tgt.addEventListener('touchend', PaintingListener.touchToMouseTile, { passive: true })
            }
        }
        wire(root.getElementsByClassName('cbox-ratio-color'))
        wire(root.getElementsByClassName('cbox-ratio-texture'))
    }

    /**
     * Delegate drop handling so other modules can reuse the shared pipeline.
     * @param {string[]} classList - Classes from the dragged helper element.
     * @param {string} swatchcolorid - Dropped swatch/texture id.
     * @param {Object} ui - jQuery UI drop payload.
     * @returns {Promise<void>}
     */
    async elementDropped(classList, swatchcolorid, ui) {
        return this.dropHandler.elementDropped(classList, swatchcolorid, ui)
    }

    // ──────────────────────────────────────────────
    // Utility helpers
    // ──────────────────────────────────────────────

    /**
     * Show a Bootstrap modal by id.
     * @param {string} id - Element id of the modal.
     */
    _showModal(id) {
        if (id === 'loginModal' || id === 'userLoggedButEmailNotVerifiedModal' || id === 'profileCompletionModal') {
            window.ensureAuthModalStack()
        }

        const el = this.root.getElementById(id)
        if (el) $(el).modal('show')
    }

    /** Remove prepaint-specific query params from the URL (used after new project save). */
    _stripPrepaintParams() {
        try {
            const u = new URL(window.location.href)
            u.searchParams.delete('prepaint_photo_id')
            u.searchParams.delete('color_sample_id')
            const qs = u.searchParams.toString()
            window.history.replaceState({}, '', u.pathname + (qs ? `?${qs}` : '') + u.hash)
        } catch (err) {
            console.warn('Failed to strip prepaint params', err)
        }
    }

    /**
     * Activate a tool in the Application tool manager.
     * @param {string} tool - Tool name (brush, eraser, hand, etc.).
     */
    _selectTool(tool) {
        this.application.toolManager.setActiveTool(tool)
    }

    /**
     * Build an order URL from `visualizer.order_button.url_template`.
     *
     * Supported placeholders:
     * - `{colors}` / `{colors_encoded}`
     * - `{qty}` / `{qty_encoded}`
     * - `{locale}` / `{locale_encoded}`
     * - `{random}`
     *
     * @param {string} template
     * @param {string[]} colors
     * @param {Array<string|number>} qty
     * @param {string} locale
     * @returns {string}
     */
    _buildOrderUrlFromTemplate(template, colors, qty, locale) {
        const colorsRaw = colors.join(',')
        const qtyRaw = qty.join(',')
        const random = String(((Math.random() * 1000000) | 0) + 1)
        const map = {
            colors: colorsRaw,
            colors_encoded: encodeURIComponent(colorsRaw),
            qty: qtyRaw,
            qty_encoded: encodeURIComponent(qtyRaw),
            locale: locale,
            locale_encoded: encodeURIComponent(locale),
            random: random,
        }

        return String(template).replace(/\{([a-z_]+)\}/gi, (match, token) => {
            const key = String(token || '').toLowerCase()
            return Object.prototype.hasOwnProperty.call(map, key) ? map[key] : match
        })
    }

    /**
     * Resolve order-button config from runtime payload.
     * @returns {{urlTemplate:string,target:string,requireColors:boolean,defaultLocale:string}}
     */
    _resolveOrderButtonConfig() {
        const cfg = this.application?.config?.visualizer?.order_button || {}
        return {
            urlTemplate: String(cfg.url_template || '').trim(),
            target: String(cfg.target || '_blank').trim() || '_blank',
            requireColors: cfg.require_colors !== false,
            defaultLocale: String(cfg.default_locale || '').trim(),
        }
    }

    /**
     * Open the external "buy" URL.
     *
     * @returns {void}
     */
    _handleBuy() {
        const app = this.application
        const [colors, qty] = Utils.getCurrentColorsCode(app)
        const orderCfg = this._resolveOrderButtonConfig()
        if (orderCfg.requireColors && colors.length === 0) {
            alert('Please select a color')
            return
        }

        if (orderCfg.urlTemplate !== '') {
            const locale = orderCfg.defaultLocale || app.currentLocale || 'en'
            const url = this._buildOrderUrlFromTemplate(orderCfg.urlTemplate, colors, qty, locale)
            if (url !== '') {
                window.open(url, orderCfg.target)
                return
            }
        }
    }

    /**
     * Delete the active dropcolor slot and shift mask indices.
     * @param {Event} event - Click event from the delete button.
     */
    _handleDeleteDropColorClick(event) {
        const app = this.application

        const btnId = event.currentTarget.id
        const slotFromBtn = parseInt(btnId.replace(Dropcolor.DELETE_BTN_ID_PREFIX, ''), 10)
        if (slotFromBtn) {
            app.canvasState.colorSample.workingColorPos = slotFromBtn
        }

        const action = new CXImageAction(CXImageAction.ActionType.ActionTypeEraseMaskColor)
        action.initWithAsEraseMaskColorActionWithColor(app.canvasState.colorSample, app.canvasState.colorSample.workingColorPos)

        const pos = app.canvasState.colorSample.workingColorPos
        $(`#dropcolor${pos}`).children('div:first').remove()
        $(`#dropcolor${pos}`).attr('style', '')
        $(`#deletedropcolor${pos}`).hide()

        Utils.hideAllSlider()

        app.canvasState.viewimage.loadData()

        const mask = app.canvasState._maskImage
        for (let i = 0; i < mask.data.length; i++) {
            const maskColor = mask.data[i]

            if (maskColor === parseInt(app.canvasState.colorSample.workingColorPos, 10)) {
                action.lastLevelpixels().addPixelwithValue(i, mask.data[i])
                mask.data[i] = Config.DEFAULT_MASKING_COLOR
                app.canvasState.refreshResultAtPosition([i, mask.data[i]])
            } else if (
                maskColor < Config.DEFAULT_MASKING_COLOR &&
                maskColor > parseInt(app.canvasState.colorSample.workingColorPos, 10)
            ) {
                mask.data[i] = maskColor - 1
            }
        }

        app.canvasState.viewimage.context.putImageData(app.canvasState.viewimage.data, 0, 0)

        app.canvasState.colorSample.deleteColorNumber(parseInt(app.canvasState.colorSample.workingColorPos, 10))
        app.canvasState.colorSample.updateColorInfos()

        app.canvasState.addAction(action)
    }
}
export default PaintingListener
